import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { TravelOption } from '..';
import {
  SearchStatus, SearchModeLabel, FlightSearchTypes, STATIC_TRIP_ID, SearchModes,
} from '../../constants';
import Map from '../TravelOption/Map';
import checkDoShowMap from '../../helpers/selectors/checkDoShowMap';
import { hotelSearchRequest, setHotelTripSearchParam } from '../../store/slices/tripSearch';
import { setIsUserInteractedWithHotelList } from '../../store/slices/hotel';
import { resetError } from '../../store/slices/trip';
import MarkdownToHtml from '../MarkdownToHtml';
import ResultsExpirationModal from '../TravelOption/ResultsExpirationModal';
import SkeletonCards from '../SkeletonCards';
import CustomBreadcrumb from './BreadCrumbs';
import TripConfiguration from '../TripConfiguration';
import checkDoSkipTripConfiguration from '../../helpers/selectors/checkDoSkipTripConfiguration';
import checkDoEnableTripConfigDecoupling from '../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import flightSearchInput from '../../helpers/selectors/flightSearchInput';
import { checkDoEnableSelectFlightsBySegment } from '../../store/slices/user';
import ChatGPTInterface from '../ChatGPTInterface';
import useHotelsSearchResultsExtractor from '../ChatGPTInterface/extractors/hotelSearchResultsExtractor';
import useHotelsSearchResultsUtils from '../ChatGPTInterface/helpers/hotelsSearchResultsUtils';

const renderMessage = (searchMode) => SearchModeLabel[searchMode].toLowerCase();
const TravelOptions = ({ doShowOnlyCruiseTab }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const [itineraryKeyToScroll, setItineraryKeyToScroll] = useState('');
  const [hoveredKey, setHoveredKey] = useState(null);
  const [firstTravelOptionTab, setFirstTravelOptionTab] = useState(undefined);
  const { searchObj, searchStatus, selectedSearchMode } = useSelector((store) => store.tripSearch);
  const { deepLinkSearchStatus } = useSelector((store) => store.hotel);
  const { hasUserInteractedWithHotelList } = useSelector((store) => store.hotel);
  const { flightSearchType } = searchObj.flight;
  const {
    breadCrumbsIndex, selectedFlightSegments,
  } = useSelector(flightSearchInput(undefined, selectedSearchMode === SearchModes.FLIGHT));
  const { travelAgency } = useSelector((store) => store.user);
  const searchNoCarsErrorMessage = travelAgency && travelAgency.settings
    && travelAgency.settings.searchNoCarsErrorMessage;
  const tripSearchLoadingHtml = travelAgency && travelAgency.settings
    && travelAgency.settings.tripSearchLoadingHtml;
  const doDisableHotelsMap = travelAgency && travelAgency.settings
    && travelAgency.settings.doDisableHotelsMap;
  const hotelSearchNoResultsError = travelAgency && travelAgency.settings
    && travelAgency.settings.hotelSearchNoResultsError;
  const doEnableSelectFlightsBySegment = useSelector(checkDoEnableSelectFlightsBySegment);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  // doShowMap set when markers available and isMapShown set in tripSearch store
  const doShowMap = useSelector(checkDoShowMap);
  const doRenderMap = !!(doShowMap && !doDisableHotelsMap);
  const {
    travelOptions,
    specificHotelNotFoundMessage,
    cachedItinerariesChanges,
    page, doHaveResultsWithoutFilter, errorMessage,
  } = useSelector((state) => state.tripSearch.searchResult);
  const querySearchMode = router.query.searchMode;
  const selectedTrip = useSelector((state) => state.trip.selectedTrips[STATIC_TRIP_ID]);
  const doShowBookButton = useSelector(checkDoSkipTripConfiguration);
  const doShowTripConfiguration = !!(!doShowBookButton && !isEmpty(selectedTrip)
    && !doShowOnlyCruiseTab);

  const doShowNoTripsMsg = searchStatus === SearchStatus.DONE
    && (!travelOptions || travelOptions.length === 0)
    && (!querySearchMode || selectedSearchMode === querySearchMode);

  const doShowNoTripsMsgForDeepLink = deepLinkSearchStatus === SearchStatus.DONE
    && searchStatus !== SearchStatus.SEARCHING && (!travelOptions || travelOptions.length === 0);
  const doShowChatbotOnHotelSearchResults = travelAgency?.settings
    ?.doShowChatbotOnHotelSearchResults;
  useEffect(() => {
    if (searchStatus === SearchStatus.DONE
      && (cachedItinerariesChanges && cachedItinerariesChanges.length)) {
      const data = {
        msg: `Cached cruise itineraries for this search has changes: ${cachedItinerariesChanges.join(', ')}`,
        ...searchObj.cruise,
      };
      // Removing properties which not required to track
      delete data.page;
      delete data.pageSize;
      delete data.sortBy;
      delete data.sortDirection;
      delete data.areFiltersEmpty;
      delete data.doReturnOnlyAddedUpdatedItineraries;

      Object.keys(data).forEach((key) => {
        if (typeof (data[key]) === 'object') {
          data[key] = JSON.stringify(data[key]);
        }
      });
      track('CachedCruiseItinerariesUpdated', data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchStatus, cachedItinerariesChanges,
  ]);

  // TODO: probably best to make it an HOC component?
  // withSkeletonLoading()(CruiseOption) or something like that.
  // maybe a lib exists for this?
  const skeletonCards = useMemo(() => SkeletonCards(3), []);

  const renderMarkdownOrSkeleton = useMemo(() => (tripSearchLoadingHtml
    ? <div className="bg-transparent mx-auto flex justify-center items-center p-2"><MarkdownToHtml content={tripSearchLoadingHtml} className="text-black text-base text-center flex flex-col items-center justify-center" /></div> : skeletonCards
  ), [skeletonCards, tripSearchLoadingHtml]);

  const handleMarkerClick = (hotelKey, pageNumber) => {
    setItineraryKeyToScroll(hotelKey);
    if (page !== pageNumber) {
      dispatch(setHotelTripSearchParam({ page: pageNumber }));
      dispatch(hotelSearchRequest());
    }
  };

  useEffect(() => {
    if (itineraryKeyToScroll && searchStatus === SearchStatus.DONE) {
      const timeout = setTimeout(() => {
        setItineraryKeyToScroll('');
      }, 2000);
      return () => clearTimeout(timeout);
    }
    return () => ({});
  }, [itineraryKeyToScroll, searchStatus]);

  const handleItineraryMouseEnter = useCallback((itineraryIndex) => (hotelKey) => {
    setHoveredKey(hotelKey);
    if (itineraryIndex >= 3 && !hasUserInteractedWithHotelList) {
      dispatch(setIsUserInteractedWithHotelList(true));
    }
  }, [dispatch, hasUserInteractedWithHotelList]);

  const handleItineraryMouseLeave = useCallback(() => {
    setHoveredKey(null);
  }, [setHoveredKey]);

  useEffect(() => {
    // On load, remove "error" which is trip booking error
    dispatch(resetError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => setFirstTravelOptionTab(undefined));

  // Set doShowTravelOptions to true even when search is in progress and doShowMap is true
  // to ensure that when the hotel map is displayed andt he user selects the hotel pin or the
  // change page, the map appears while the search is in progress.
  const doShowTravelOptions = (searchStatus === SearchStatus.DONE
    || (searchStatus === SearchStatus.SEARCHING && doShowMap))
    && travelOptions && travelOptions.length > 0 && !doShowNoTripsMsg;
  const doShowTravelOptionsWithDeeplink = (deepLinkSearchStatus === SearchStatus.DONE
    || deepLinkSearchStatus === SearchStatus.SEARCHING) && !doShowNoTripsMsgForDeepLink;

  const mainGridColumns = () => {
    let cols;
    if (!doEnableTripConfigDecoupling) cols = 'col-span-7';
    if (doRenderMap && doShowTripConfiguration) cols = 'col-span-8';
    if (!doRenderMap && doShowTripConfiguration) cols = 'col-span-9';
    return cols ?? 'col-span-7';
  };

  const sideGridColumns = () => {
    let cols;
    if (!doEnableTripConfigDecoupling) cols = 'col-span-5';
    if (doRenderMap && doShowTripConfiguration) cols = 'col-span-4';
    if (!doRenderMap && doShowTripConfiguration) cols = 'col-span-3';
    return cols ?? 'col-span-5';
  };
  const [selectedTabIndexes, setSelectedTabIndexes] = useState(() => (
    travelOptions && travelOptions.length
      ? Array(travelOptions.length).fill(parseInt(selectedSearchMode, 10))
      : []
  ));
  const setSelectedTabIndex = (index, value) => {
    if (!travelOptions || index < 0 || index >= travelOptions.length) return;
    setSelectedTabIndexes((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const { generalInfo, utilFunctions } = useHotelsSearchResultsUtils();
  return (
    <>
      {doShowChatbotOnHotelSearchResults
      && utilFunctions && generalInfo && (
        <ChatGPTInterface
          rawData={{ generalInfo }}
          extractor={useHotelsSearchResultsExtractor}
          utilFunctions={utilFunctions}
          generalIntro={generalInfo}
          siteType="SearchResults"
          newDesign
          instructionName="Interface - Search Results Instruction"
        />
      )}
      {doEnableSelectFlightsBySegment
      && selectedSearchMode === SearchModes.FLIGHT
      && (flightSearchType === FlightSearchTypes.MULTI_CITY
          || flightSearchType === FlightSearchTypes.ROUND_TRIP)
      && selectedTabIndexes[0] === parseInt(selectedSearchMode, 10)
      && (
        <CustomBreadcrumb />
      )}
      {(doShowTravelOptions || doShowTravelOptionsWithDeeplink) && (
        <div id="main-section-wrapper" className={clsx({ 'flex flex-col md:grid md:grid-cols-12 md:gap-4': doRenderMap || doShowTripConfiguration })}>
          {specificHotelNotFoundMessage && (
            <div className="mb-1 col-span-12">
              <div className={clsx('bg-orange-200 px-4 py-2 rounded-lg border border-orange-400 min-w-full mb-4 text-black text-center', { 'sm:mb-0': doRenderMap })}>
                <p>{specificHotelNotFoundMessage}</p>
              </div>
            </div>
          )}

          <div className={clsx(mainGridColumns(), 'order-2 md:order-none')}>
            <ResultsExpirationModal />
            {(searchStatus || deepLinkSearchStatus) === SearchStatus.SEARCHING
              ? renderMarkdownOrSkeleton : travelOptions.map((travelOption, index) => (
                <TravelOption
                  key={travelOption.key}
                  tripId={doEnableTripConfigDecoupling ? STATIC_TRIP_ID : travelOption.key}
                  travelOption={travelOption}
                  doShowOnlyCruiseTab={doShowOnlyCruiseTab}
                  onItineraryMouseEnter={handleItineraryMouseEnter(index)}
                  onItineraryMouseLeave={handleItineraryMouseLeave}
                  isExpanded={{ value: itineraryKeyToScroll === travelOption.key }}
                  travelOptionIndex={index}
                  firstTravelOptionTab={firstTravelOptionTab}
                  selectedTab={selectedTabIndexes[index]}
                  setSelectedTab={setSelectedTabIndex}
                />
              ))}
          </div>
          {(doShowTripConfiguration || doRenderMap) && (
            <div className={clsx(sideGridColumns(), 'order-1 md:order-none')}>
              {doShowTripConfiguration && doEnableTripConfigDecoupling && (
                <TripConfiguration
                  tripId={STATIC_TRIP_ID}
                  onTabChange={(tab) => setFirstTravelOptionTab(tab)}
                />
              )}
              {doRenderMap && (
                <Map
                  hoveredMarkerKey={hoveredKey}
                  onMarkerClick={handleMarkerClick}
                />
              )}
            </div>
          )}
        </div>
      )}
      {(doShowNoTripsMsg || doShowNoTripsMsgForDeepLink) && (
        <div className="bg-white shadow-sm p-5 rounded leading-8">
          {doHaveResultsWithoutFilter && (
            <span>
              {`No ${renderMessage(selectedSearchMode)} found for your filter criteria.`}
            </span>
          )}
          {!doHaveResultsWithoutFilter && renderMessage(selectedSearchMode) === 'hotels' && (
            <div>
              <h1 className="text-lg mb-5">
                {hotelSearchNoResultsError
                  || `We couldn't find any available ${renderMessage(selectedSearchMode)}
                matching your criteria.`}
              </h1>
              <p>Here are some suggestions to help you find the perfect stay:</p>
              <ol className="list-disc pl-5">
                <li>
                  <strong>Broaden your search:</strong>
                  {' '}
                  Try expanding your search area or removing some filters.
                  Sometimes, widening your search can reveal more options.
                </li>
                <li>
                  <strong>Adjust filters:</strong>
                  {' '}
                  Review your selected filters, such as star rating or preferred rate codes.
                  Relaxing some of these criteria might yield more results.
                </li>
                <li>
                  <strong>Change dates:</strong>
                  {' '}
                  The availability of hotels can vary by date. Consider adjusting your
                  check-in and check-out dates for more options.
                </li>
                <li>
                  <strong>Explore nearby areas:</strong>
                  {' '}
                  If you&apos;re flexible with your location, explore nearby neighborhoods
                  or cities to discover alternative accommodations.
                </li>
                <li>
                  <strong>Contact support:</strong>
                  {' '}
                  If you still can&apos;t find what you&apos;re looking for, our
                  friendly support team is here to assist you. Feel free to
                  reach out to support@travelwits.com for personalized assistance.
                </li>
              </ol>
            </div>
          )}
          {!doHaveResultsWithoutFilter && renderMessage(selectedSearchMode) !== 'hotels' && (renderMessage(selectedSearchMode) === 'cars' && searchNoCarsErrorMessage ? searchNoCarsErrorMessage : `No ${renderMessage(selectedSearchMode)} found for your input.`)}
          {' '}
          {selectedSearchMode === SearchModes.FLIGHT && breadCrumbsIndex > 0 && Object.keys(selectedFlightSegments).length > 0 && 'Try changing one of your earlier flights to see available options.'}
          {errorMessage ? ` ${errorMessage}` : ''}
        </div>
      )}
      {!doShowTravelOptions && searchStatus === SearchStatus.SEARCHING && renderMarkdownOrSkeleton}
    </>
  );
};

TravelOptions.propTypes = {
  doShowOnlyCruiseTab: PropTypes.bool,
};

TravelOptions.defaultProps = {
  doShowOnlyCruiseTab: false,
};
export default TravelOptions;
